import { Box, Button, Checkbox, Divider, FormControlLabel, Radio, TextField, Typography } from "@material-ui/core";
import { ExpandLess, ExpandMore, Search } from "@material-ui/icons";
import React, { memo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { firstLetterUppercase } from "../../utils/first-letter-uppercase";
import { groupBy } from "../../utils/group-by";
import CheckedRadio from "../icons/Controls/CheckedRadio";
import UncheckedRadio from "../icons/Controls/UncheckedRadio";
import { VirtualScroll } from "../VirtualScroll";
import { FlexierTypography } from "./FlexierTypography";
import {
	filterDialogVirtualScrollThresholdItems,
	initiallyShownOptionsCount,
	ListFilterOption,
	ListFilterState,
	TableFilterAction,
} from "./models";
import NestedListFilter from "./NestedListFilter";
import useStyles from "./styles";
import SelectAllCheckbox from "./SelectAllCheckbox";
import { useDebounce } from "@lyfegen-platform/react-common";
import CheckboxIcon from "../icons/CheckboxIcon";

function SearchControl(props: {
	filter: ListFilterState<any>;
	usePopover?: boolean;
	parentCode?: string;
	nested?: boolean;
	dispatcher: (action: TableFilterAction) => void;
}) {
	const [searchTerm, setSearchTerm] = useState(props.filter.searchTerm || "");
	const debouncedTerm = useDebounce(searchTerm);
	const { t } = useTranslation();
	const { customTextField } = useStyles();

	useEffect(() => {
		props.dispatcher({ type: "search-list-value", path: props.filter.field as string, term: debouncedTerm });
	}, [debouncedTerm]);

	return (
		<TextField
			id={`${props.filter.field as string}-search`}
			fullWidth
			classes={{ root: customTextField }}
			placeholder={
				!props.usePopover && props.filter.searchLabel
					? t(props.filter.searchLabel, { code: props.parentCode })
					: (firstLetterUppercase(t("search")) as string)
			}
			InputProps={{
				disableUnderline: true,
				startAdornment: <Search style={{ marginRight: "4px" }} color="primary" fontSize="small" />,
			}}
			value={searchTerm}
			onChange={(e) => setSearchTerm(e.target.value)}
		/>
	);
}

interface IFilterOptionProps {
	filter: ListFilterState;
	option: ListFilterOption;
	usePopover?: boolean;
	isGrouped: boolean;
	customOnSelectAction?: () => void;
	handlePopoverClose?: () => void;
	dispatcher: (action: TableFilterAction) => void;
}

const FilterOption: React.FC<IFilterOptionProps> = ({
	filter,
	option,
	usePopover,
	isGrouped,
	dispatcher,
	customOnSelectAction,
	handlePopoverClose,
}) => {
	const { listItemControl, ellipsisRule, controlLabel, fullWidth } = useStyles();
	function handleDispatchNestedFilterAction(optionsValue: string) {
		return (nestedFilterAction: TableFilterAction) =>
			dispatcher({
				...nestedFilterAction,
				path: `${filter.field as string}|${optionsValue}`,
			} as TableFilterAction);
	}

	const autoCloseAfterSelect = () => {
		if (!handlePopoverClose || option.nested || filter.isMulti) {
			return;
		}

		handlePopoverClose();
	};

	return (
		<>
			<FormControlLabel
				value={option.value}
				control={
					filter.isMulti ? (
						<Checkbox icon={<CheckboxIcon />} className={listItemControl} />
					) : (
						<Radio icon={<UncheckedRadio />} checkedIcon={<CheckedRadio />} className={listItemControl} />
					)
				}
				checked={!!option.isSelected}
				onChange={() => {
					dispatcher({
						type: "select-list-value",
						value: option.value,
						path: filter.field as string,
					});
					customOnSelectAction && customOnSelectAction();
					autoCloseAfterSelect();
				}}
				label={
					filter.renderOption ? (
						filter.renderOption(option)
					) : (
						<FlexierTypography
							className={ellipsisRule}
							color={option.isSelected ? "primary" : undefined}
							style={{ maxWidth: isGrouped ? "calc(100% - 30px)" : undefined }}
						>
							{option.label}
						</FlexierTypography>
					)
				}
				classes={{ root: isGrouped ? fullWidth : controlLabel, label: isGrouped ? fullWidth : undefined }}
			/>
			{option.nested && option.isSelected && (
				<NestedListFilter
					filter={option.nested}
					parentCode={option.label.split(" ")[0]}
					usePopover={usePopover}
					dispatcher={handleDispatchNestedFilterAction(option.value)}
				/>
			)}
		</>
	);
};

const optionGroupSize = 3;

function ListFilter(props: {
	isTabsGroup?: boolean;
	nested?: boolean;
	filter: ListFilterState;
	usePopover: boolean;
	parentCode?: string;
	customOnSelectAction?: () => void;
	handlePopoverClose?: () => void;
	dispatcher: (action: TableFilterAction) => void;
}) {
	const { t } = useTranslation();
	const { scrollable, divider, textButton } = useStyles();
	const filteredOptions = props.filter.options.filter((option) => !option.isFilteredOut);
	const verticalListColumn = "status";
	const hasMore =
		!props.usePopover &&
		(props.filter.shownItemCount ?? 0) < filteredOptions.length &&
		filteredOptions.length > initiallyShownOptionsCount;
	const shownOptions = props.usePopover
		? filteredOptions
		: filteredOptions.slice(0, props.filter.shownItemCount || initiallyShownOptionsCount);
	const isGrouped = !props.usePopover && !props.filter.hasNestedOrNested && !props.filter.field.toString().includes(verticalListColumn);
	const optionElements = shownOptions.map((option) => (
		<Box
			key={option.value}
			display={props.usePopover ? "flex" : undefined}
			alignItems="center"
			justifyContent="space-between"
			height={props.filter.hasNestedOrNested ? undefined : "2rem"}
			minHeight={props.filter.hasNestedOrNested ? "2rem" : undefined}
			minWidth="33%"
			width={isGrouped ? "33%" : undefined}
			mb={props.usePopover ? undefined : 1}
		>
			<FilterOption
				filter={props.filter}
				option={option}
				usePopover={props.usePopover}
				isGrouped={isGrouped}
				dispatcher={props.dispatcher}
				customOnSelectAction={props.customOnSelectAction}
				handlePopoverClose={props.handlePopoverClose}
			/>
		</Box>
	));

	const optionGroups = isGrouped
		? Array.from(groupBy(optionElements, (_, index) => Math.floor((index || 0) / optionGroupSize))).map(([key, group]) => (
				<Box 
					key={key} 
					display="flex" 
					flexDirection={props.isTabsGroup && !props.usePopover ? "column" : undefined}
					width="100%" 
					height={props.isTabsGroup && !props.usePopover ? "auto" : "2rem"}
				>
					{group}
				</Box>
		  ))
		: optionElements;
	const isVirtualScrollEnabled = !props.filter.hasNestedOrNested && shownOptions.length > filterDialogVirtualScrollThresholdItems;

	return (
		<Box width="100%" minWidth="200px">
			{!props.filter.nonSearchable && (
				<>
					<Box mx={1}>
						<SearchControl
							filter={props.filter}
							nested={props.nested}
							parentCode={props.parentCode}
							usePopover={props.usePopover}
							dispatcher={props.dispatcher}
						/>
					</Box>
					{props.usePopover && <Divider className={divider} />}
				</>
			)}
			<Box mx={1}>
				<SelectAllCheckbox filter={props.filter} dispatcher={props.dispatcher} />
			</Box>
			<Box
				px={1}
				maxHeight={props.usePopover ? "20rem" : undefined}
				display={props.usePopover || props.filter.hasNestedOrNested || !props.filter.isMulti ? undefined : "flex"}
				flexDirection={props.isTabsGroup && !props.usePopover ? "column" : undefined}
				width="100%"
				flexWrap="wrap"
				style={{ overflowY: isVirtualScrollEnabled ? "hidden" : "auto" }}
				className={scrollable}
			>
				<VirtualScroll
					width={props.usePopover ? "20rem" : "100%"}
					height={props.usePopover ? 20 : 200}
					unit={"rem"}
					visibleItemCount={props.usePopover ? 10 : 100}
					enabled={isVirtualScrollEnabled}
				>
					{optionGroups}
				</VirtualScroll>
				{shownOptions.length === 0 && props.filter.searchTerm && (
					<Typography color="textSecondary">{t("no_options_available")}</Typography>
				)}
			</Box>
			{(props.filter.shownItemCount ?? 0) > initiallyShownOptionsCount && (
				<Button
					variant="text"
					color="primary"
					className={textButton}
					style={{ textTransform: "none", marginLeft: "6px", marginBottom: "6px" }}
					onClick={() => props.dispatcher({ type: "show-less", path: props.filter.field as string })}
				>
					<Box display="flex" alignItems="center">
						<ExpandLess color="primary" />
						<Typography style={{ marginLeft: "4px", fontSize: "14px" }} variant="caption" color="primary">
							{t("show-less")}
						</Typography>
					</Box>
				</Button>
			)}
			{hasMore && (
				<Button
					variant="text"
					color="primary"
					style={{ textTransform: "none", marginLeft: "6px", marginBottom: "6px" }}
					className={textButton}
					onClick={() => props.dispatcher({ type: "load-more", path: props.filter.field as string })}
				>
					<Box display="flex" alignItems="center">
						<ExpandMore color="primary" />
						<Typography style={{ marginLeft: "4px", fontSize: "14px" }} variant="caption" color="primary">
							{t(props.filter.loadMoreLabel || "load-more", { code: props.parentCode })}
						</Typography>
					</Box>
				</Button>
			)}
		</Box>
	);
}

export default memo(ListFilter);
